"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.collectMetadata = collectMetadata;
exports.collectIO = collectIO;
const conventions_1 = require("../../conventions");
const json_1 = require("../utils/json");
const helpers_1 = require("./helpers");
const part_1 = require("../normalize/part");
const message_1 = require("../normalize/message");
const finish_reason_1 = require("../normalize/finish-reason");
const format_1 = require("../utils/format");
function collectToolDefinitionsFromRecord(record, accumulator) {
    const toolsFullyParsed = (0, helpers_1.addToolDefinitionValue)(accumulator, record.tools, {
        allowProviderToolWithoutName: true,
        allowToolMap: true,
    });
    if ((0, format_1.isToolDefinitionMessage)(record)) {
        (0, helpers_1.addToolDefinitionValue)(accumulator, record.content);
    }
    return toolsFullyParsed;
}
function collectRootToolDefinitions(root, accumulator) {
    if (collectToolDefinitionsFromRecord(root, accumulator)) {
        (0, json_1.recordKeyAsParsed)(root, "tools");
    }
    // Definitions are independent of the message claim. A record can contain
    // an OpenAI `choices` carrier and, next to it, a framework `messages` or
    // `contents` carrier with additional declarations. Inspect only these
    // known carrier boundaries; do not recursively walk arbitrary payloads.
    for (const key of [
        "messages",
        "choices",
        "candidates",
        "contents",
        "output",
        "new_message",
    ]) {
        const parsed = (0, json_1.parseIfString)(root[key]);
        const candidate = (0, json_1.parseRecord)(parsed);
        const values = (() => {
            if (Array.isArray(parsed)) {
                return parsed;
            }
            if (candidate) {
                return [candidate];
            }
            return [];
        })();
        for (const value of values) {
            const record = (0, json_1.asRecord)(value);
            if (!record)
                continue;
            collectToolDefinitionsFromRecord(record, accumulator);
            for (const nestedKey of ["message", "content", "parts"]) {
                const nested = (0, json_1.parseRecord)(record[nestedKey]);
                if (nested)
                    collectToolDefinitionsFromRecord(nested, accumulator);
            }
        }
    }
    for (const provider of conventions_1.registeredProviders) {
        const sources = provider.collectToolDefinitionSources?.({ root }) ?? [];
        for (const source of sources) {
            (0, helpers_1.addToolDefinitionValue)(accumulator, source.value, source.options);
        }
    }
}
function collectMetadataToolDefinitions(metadata, accumulator) {
    (0, helpers_1.addToolDefinitionValue)(accumulator, metadata.tools);
    const attributes = (0, json_1.parseRecord)(metadata.attributes);
    if (!attributes)
        return;
    (0, helpers_1.addToolDefinitionValue)(accumulator, attributes.tools);
    for (const provider of conventions_1.registeredProviders) {
        const sources = provider.collectToolDefinitionSources?.({
            metadataAttributes: attributes,
        }) ?? [];
        for (const source of sources) {
            (0, helpers_1.addToolDefinitionValue)(accumulator, source.value, source.options);
        }
    }
}
function collectMessageSequence(values, fallbackRole, parserContext, messages, accumulator) {
    const { source } = parserContext;
    const standaloneToolCalls = [];
    const flushStandaloneToolCalls = () => {
        if (standaloneToolCalls.length === 0)
            return;
        (0, helpers_1.addMessage)(messages, {
            role: "assistant",
            parts: standaloneToolCalls.splice(0),
            source,
        }, parserContext);
    };
    for (const value of values) {
        // Some providers return a list of messages, rather than a single message.
        if (Array.isArray(value)) {
            flushStandaloneToolCalls();
            collectMessageSequence(value, fallbackRole, parserContext, messages, accumulator);
            continue;
        }
        const record = (0, json_1.asRecord)(value);
        if (record)
            collectToolDefinitionsFromRecord(record, accumulator);
        // OpenAI Responses tool listings are declarations, not conversation
        // content. Keep them side-band without breaking an adjacent call batch.
        if (record?.type === "mcp_list_tools")
            continue;
        if (record && !(0, format_1.isMessageLike)(record)) {
            const parts = (0, part_1.normalizePartValue)(record, parserContext);
            const onlyToolCalls = parts.length > 0 && parts.every((part) => part.type === "tool-call");
            if (onlyToolCalls) {
                standaloneToolCalls.push(...parts);
                continue;
            }
        }
        flushStandaloneToolCalls();
        const message = (0, message_1.normalizeMessage)(value, fallbackRole, parserContext);
        if (message)
            (0, helpers_1.addMessage)(messages, message, parserContext);
    }
    flushStandaloneToolCalls();
}
function emitRootSource(rootSource, parserContext, messages, accumulator) {
    if (rootSource.kind === "sequence") {
        collectMessageSequence(rootSource.values, rootSource.fallbackRole, parserContext, messages, accumulator);
        return;
    }
    const message = (0, message_1.normalizeMessage)(rootSource.value, rootSource.fallbackRole, parserContext);
    if (!message)
        return;
    const finishReason = (rootSource.finishReasonCarrier
        ? (0, finish_reason_1.normalizeFinishReason)(rootSource.finishReasonCarrier)
        : undefined) ?? message.finishReason;
    (0, helpers_1.addMessage)(messages, {
        ...message,
        role: rootSource.roleOverride ?? message.role,
        ...(finishReason ? { finishReason } : {}),
    }, parserContext);
}
function findSystemMessageSources(root, source) {
    const sources = [];
    for (const provider of conventions_1.registeredProviders) {
        const systemMessage = provider.getSystemMessage?.(root, source);
        if (systemMessage)
            sources.push(systemMessage);
    }
    return sources;
}
function claimMessages(root, source, parserContext) {
    for (const provider of conventions_1.registeredProviders) {
        const sources = provider.claimMessages?.(root, source) ?? [];
        if (sources.length > 0) {
            parserContext.preferredProvider = provider;
            return sources;
        }
    }
    return [];
}
function collectRecordMessages(root, parserContext, accumulator) {
    const { source } = parserContext;
    const fallbackRole = source === "input" ? "user" : "assistant";
    const systemSources = source === "input" ? findSystemMessageSources(root, source) : [];
    const claimedSources = claimMessages(root, source, parserContext);
    const rootMessages = (0, json_1.parseArray)(root.messages);
    const paramsMessages = source === "input"
        ? (0, json_1.parseArray)((0, json_1.parseRecord)(root.params)?.messages)
        : undefined;
    const hasMessageShapedParams = paramsMessages?.some((value) => {
        const record = (0, json_1.asRecord)(value);
        return record !== undefined && (0, format_1.isMessageLike)(record);
    });
    const messages = [];
    if (claimedSources.length > 0) {
        for (const rootSource of claimedSources) {
            emitRootSource(rootSource, parserContext, messages, accumulator);
        }
    }
    else if (rootMessages) {
        (0, json_1.recordKeyAsParsed)(root, "messages");
        collectMessageSequence(rootMessages, fallbackRole, parserContext, messages, accumulator);
    }
    else if (paramsMessages && hasMessageShapedParams) {
        (0, json_1.recordKeyAsParsed)(root, "params", "messages");
        collectMessageSequence(paramsMessages, fallbackRole, parserContext, messages, accumulator);
    }
    else {
        const recordMessage = (0, message_1.normalizeMessage)(root, fallbackRole, parserContext);
        if (recordMessage) {
            (0, helpers_1.addMessage)(messages, recordMessage, parserContext);
            delete accumulator.unparsedKeys[source];
        }
    }
    // System instructions supplement the selected input conversation. Defer
    // them until after message normalization so an existing system message
    // suppresses every top-level system sidecar.
    if (source === "input" && !parserContext.hasSystemMessage) {
        const systemMessages = [];
        for (const systemSource of systemSources) {
            emitRootSource(systemSource, parserContext, systemMessages, accumulator);
            if (systemMessages.length > 0)
                break;
        }
        messages.unshift(...systemMessages);
    }
    accumulator.messages.push(...messages);
}
function collectMetadata(parsedValue, accumulator) {
    if (parsedValue.record) {
        collectMetadataToolDefinitions(parsedValue.record, accumulator);
    }
    return;
}
function collectIO(parsedValue, context, accumulator) {
    const { source } = context;
    const fallbackRole = source === "input" ? "user" : "assistant";
    if (Array.isArray(parsedValue.value)) {
        collectMessageSequence(parsedValue.value, fallbackRole, context, accumulator.messages, accumulator);
        return;
    }
    if (parsedValue.record) {
        const unparsedKeys = { ...parsedValue.record };
        accumulator.unparsedKeys[source] = unparsedKeys;
        collectRootToolDefinitions(unparsedKeys, accumulator);
        collectRecordMessages(unparsedKeys, context, accumulator);
        return;
    }
    const message = (0, message_1.normalizeMessage)(parsedValue.value, fallbackRole, context);
    if (message)
        (0, helpers_1.addMessage)(accumulator.messages, message, context);
}
